import { useState, useCallback, useMemo } from "react";
import { isProductItem } from "@icoach/accountBook/createOrder/CreateOrderPage.util";

/**
 * @typedef {Object} RawProductData - API 返回的原始商品資料
 * @property {number} productID
 * @property {number} [variantID]
 * @property {string} productName
 * @property {string} [variantName]
 * @property {string} sku
 * @property {number} price
 * @property {number} fixedPrice
 * @property {string} pictureSrc
 * @property {number} stock
 * @property {boolean} isLimited
 * @property {string} [productTypeText]
 */

/**
 * @typedef {Object} ProductItem - 購物車中的商品項目
 * @property {number} productID
 * @property {number} [variantID]
 * @property {string} productName
 * @property {string} [variantName]
 * @property {string} sku
 * @property {string} pictureSrc
 * @property {string} [productTypeText]
 * @property {number} price
 * @property {number} fixedPrice
 * @property {number} assignPrice
 * @property {number} quantity
 * @property {number} stock
 * @property {boolean} isLimited
 * @property {string} notes
 * @property {boolean} [isPriceEditable] - 是否允許編輯價格（預設為 true）
 * @property {boolean} [allowDelete] - 是否允許刪除商品（預設為 true）
 * @property {boolean} [allowZeroQuantity] - 是否允許數量為0（預設為 false）
 */

/**
 * @typedef {Object} RawFeeData - 選單中的原始費用資料
 * @property {string} text
 * @property {string} value
 * @property {string} [productTypeText]
 */

/**
 * @typedef {Object} FeeItem - 購物車中的費用項目
 * @property {string} name
 * @property {string} productTypeText
 * @property {number} assignPrice
 * @property {string} revenueType
 * @property {string} notes
 * @property {string} [relatedFeeName] - 關聯的費用名稱（用於智能月費關聯既存月費）
 */

/**
 * @typedef {ProductItem|FeeItem} CartItem
 */

/**
 * 獲取費用的識別名稱
 * @param {FeeItem | RawFeeData} item
 * @returns {string}
 */
const getFeeIdentifier = item => {
	// FeeItem 使用 name，RawFeeData 使用 text
	if (item.hasOwnProperty("name")) {
		return item.name;
	} else {
		return item.text;
	}
};

/**
 * 檢查是否為 RawFeeData（需要初始化）
 * RawFeeData 有 text 屬性，FeeItem 有 name 屬性
 * @param {FeeItem|RawFeeData} item
 * @returns {boolean}
 */
const isRawFeeData = item => {
	return item.hasOwnProperty("text") && !item.hasOwnProperty("name");
};

/**
 * 初始化費用品項資料
 * @param {RawFeeData} item
 * @returns {FeeItem}
 */
const initFeeItemData = item => {
	return {
		name: item.text,
		assignPrice: 0,
		revenueType: item.value,
		productTypeText: item.productTypeText,
		notes: "",
	};
};

/**
 * 檢查兩個商品是否為同一項目
 * @param {ProductItem|RawProductData} product1
 * @param {ProductItem|RawProductData} product2
 * @returns {boolean}
 */
const isSameProduct = (product1, product2) => {
	// productID 必須相同
	if (product1.productID !== product2.productID) {
		return false;
	}

	const variantID1 = product1.variantID;
	const variantID2 = product2.variantID;

	// 兩者都有 variantID，必須完全匹配
	if (variantID1 && variantID2) {
		return variantID1 === variantID2;
	}

	// 兩者都沒有 variantID，只匹配 productID
	if (!variantID1 && !variantID2) {
		return true;
	}

	// 一個有 variantID 另一個沒有，視為不同項目
	return false;
};

/**
 * 檢查兩個費用是否為同一項目
 * @param {FeeItem|RawFeeData} fee1
 * @param {FeeItem|RawFeeData} fee2
 * @returns {boolean}
 */
const isSameFee = (fee1, fee2) => {
	const name1 = getFeeIdentifier(fee1);
	const name2 = getFeeIdentifier(fee2);
	return name1 === name2;
};

/**
 * 檢查購物車項目是否應該被保留
 * @param {CartItem} item
 * @param {CartItem|RawProductData|RawFeeData} targetItem
 * @returns {boolean}
 */
const shouldKeepItem = (item, targetItem) => {
	const isItemProduct = isProductItem(item);
	const isTargetProduct = isProductItem(targetItem);

	// 類型不匹配，保留該項目
	if (isItemProduct !== isTargetProduct) {
		return true;
	}

	// 兩者都是商品
	if (isItemProduct && isTargetProduct) {
		const product = item;
		const targetProduct = targetItem;
		return !isSameProduct(product, targetProduct);
	}

	const fee = item;
	const targetFee = targetItem;
	return !isSameFee(fee, targetFee);
};

/**
 * 過濾購物車中的指定品項
 * @param {CartItem[]} cart
 * @param {CartItem|RawProductData|RawFeeData} cartItem
 * @returns {CartItem[]}
 */
const filterCartItem = (cart, cartItem) => {
	return cart.filter(item => shouldKeepItem(item, cartItem));
};

/**
 * 訂單購物車 Hook
 * @param {CartItem[]} [initialCartData=[]]
 * @returns {Object} 購物車狀態與操作方法
 */
export const useOrderCart = (initialCartData = []) => {
	// 購物車資料狀態
	const [cartItems, setCartItems] = useState(() => {
		return initialCartData ? [...initialCartData] : [];
	});

	/**
	 * 檢查產品是否在購物車中
	 * @param {number} productID
	 * @param {number} [variantID]
	 * @returns {boolean}
	 */
	const isProductInCart = useCallback(
		(productID, variantID) => {
			return cartItems.some(item => {
				// 只檢查商品項目
				if (!isProductItem(item)) {
					return false;
				}

				const product = item;
				const targetProduct = { productID, variantID };

				return isSameProduct(product, targetProduct);
			});
		},
		// eslint-disable-next-line
		[cartItems],
	);

	/**
	 * 檢查費用品項是否在購物車中
	 * @param {string} name
	 * @returns {boolean}
	 */
	const isFeeInCart = useCallback(
		name => {
			return cartItems.some(item => {
				// 只檢查費用品項
				if (isProductItem(item)) {
					return false;
				}

				const feeName = getFeeIdentifier(item);
				return feeName === name;
			});
		},
		// eslint-disable-next-line
		[cartItems],
	);

	/**
	 * 添加商品到購物車
	 * @param {RawProductData|RawFeeData|ProductItem|FeeItem} cartItem
	 */
	const addItem = useCallback(
		cartItem => {
			setCartItems(prev => {
				let item = cartItem;
				if (!isProductItem(item)) {
					// 費用品項處理
					if (isRawFeeData(item)) {
						// RawFeeData -> FeeItem（需要初始化）
						item = initFeeItemData(item);
					} else {
						// 已經是 FeeItem，保留所有屬性（包括 relatedFeeName 等自定義屬性）
						// 確保必要欄位有預設值，同時保留其他所有屬性
						item = {
							...item,
							name: item.name || "",
							assignPrice: item.assignPrice ?? 0,
							revenueType: item.revenueType || "",
							productTypeText: item.productTypeText || "",
							notes: item.notes || "",
						};
					}
				} else {
					// 商品品項初始化 (RawProductData -> ProductItem)
					const productItem = item;
					const price = productItem.price || 0;

					// 檢查是否已經是 ProductItem（有 assignPrice、quantity、notes）
					const isAlreadyProductItem = productItem.assignPrice !== undefined;
					const assignPrice = isAlreadyProductItem ? productItem.assignPrice : price;
					const quantity = isAlreadyProductItem ? productItem.quantity : 1;
					const notes = isAlreadyProductItem ? productItem.notes : "";

					item = {
						...productItem,
						assignPrice: assignPrice ?? price,
						quantity: quantity ?? 1,
						notes: notes ?? "",
					};
				}
				return [...prev, item];
			});
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 從購物車移除商品
	 * @param {CartItem|RawProductData|RawFeeData} cartItem - 商品需 productID/variantID，費用需 name/text
	 */
	const removeItem = useCallback(
		cartItem => {
			setCartItems(prev => filterCartItem(prev, cartItem));
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 清除購物車
	 */
	const clearCart = useCallback(
		() => {
			setCartItems([]);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 設置購物車資料
	 * @param {CartItem[]} newCartData
	 */
	const setCart = useCallback(
		newCartData => {
			setCartItems([...newCartData]);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 根據識別符找到對應的購物車項目
	 * @param {CartItem[]} cart
	 * @param {Object} identifier - 商品：{productID, variantID?}，費用：{name} 或 {text}
	 * @returns {CartItem|null}
	 */
	const findItemByIdentifier = useCallback(
		(cart, identifier) => {
			const isIdentifierProduct = isProductItem(identifier);

			for (const item of cart) {
				const isItemProduct = isProductItem(item);

				// 不同類型直接跳過
				if (isItemProduct !== isIdentifierProduct) {
					continue;
				}

				// 兩者都是商品
				if (isItemProduct && isIdentifierProduct) {
					const product = item;
					const targetProduct = identifier;

					if (isSameProduct(product, targetProduct)) {
						return item;
					}
				} else {
					// 兩者都是費用
					const fee = item;
					const targetFee = identifier;

					if (isSameFee(fee, targetFee)) {
						return item;
					}
				}
			}

			return null;
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 更新購物車中項目的價格
	 * @param {Object} identifier - 商品：{productID, variantID?}，費用：{name} 或 {text}
	 * @param {number} assignPrice
	 */
	const updatePrice = useCallback(
		(identifier, assignPrice) => {
			setCartItems(prev => {
				const targetItem = findItemByIdentifier(prev, identifier);
				if (!targetItem) return prev;

				return prev.map(item => {
					if (item === targetItem) {
						return { ...item, assignPrice: Number(assignPrice) || 0 };
					}
					return item;
				});
			});
		},
		// eslint-disable-next-line
		[findItemByIdentifier],
	);

	/**
	 * 更新購物車中商品項目的數量
	 * @param {Object} identifier - {productID, variantID?}
	 * @param {number|string} quantity - 數量值（可能是空字串、null、undefined）
	 */
	const updateQuantity = useCallback(
		(identifier, quantity) => {
			setCartItems(prev => {
				const targetItem = findItemByIdentifier(prev, identifier);
				if (!targetItem || !isProductItem(targetItem)) return prev;

				return prev.map(item => {
					if (item === targetItem) {
						// 檢查是否為空值或無效值
						const isEmptyValue = quantity === "" || quantity === null || quantity === undefined;
						const numQuantity = Number(quantity);
						const isInvalidNumber = isNaN(numQuantity);

						// 如果是空值或無效值，根據 allowZeroQuantity 決定預設值
						if (isEmptyValue || isInvalidNumber) {
							const allowZero = item.allowZeroQuantity === true;
							const defaultQuantity = allowZero ? 0 : 1;
							return { ...item, quantity: defaultQuantity };
						}

						// 有效數值，直接使用
						return { ...item, quantity: numQuantity };
					}
					return item;
				});
			});
		},
		// eslint-disable-next-line
		[findItemByIdentifier],
	);

	/**
	 * 更新購物車中項目的備註
	 * @param {Object} identifier - 商品：{productID, variantID?}，費用：{name} 或 {text}
	 * @param {string} notes
	 */
	const updateNotes = useCallback(
		(identifier, notes) => {
			setCartItems(prev => {
				const targetItem = findItemByIdentifier(prev, identifier);
				if (!targetItem) return prev;

				return prev.map(item => {
					if (item === targetItem) {
						return { ...item, notes: notes || "" };
					}
					return item;
				});
			});
		},

		// eslint-disable-next-line
		[findItemByIdentifier],
	);

	/**
	 * 刪除購物車中的單個項目
	 * @param {CartItem|RawProductData|RawFeeData} itemToDelete - 商品需 productID/variantID，費用需 name/text
	 */
	const deleteItem = useCallback(
		itemToDelete => {
			setCartItems(prev => filterCartItem(prev, itemToDelete));
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 根據條件刪除購物車中的項目
	 * 允許外部自定義刪除邏輯，例如根據 relatedFeeName 刪除關聯項目
	 * @param {Function} predicate - 條件函數，返回 true 表示要刪除該項目
	 * @example
	 * // 刪除所有 relatedFeeName 為 "2025年11月 既存月費" 的項目
	 * deleteItemsWhere(item => item.relatedFeeName === "2025年11月 既存月費")
	 */
	const deleteItemsWhere = useCallback(
		predicate => {
			setCartItems(prev => {
				try {
					return prev.filter(item => !predicate(item));
				} catch (error) {
					console.error("刪除項目時發生錯誤:", error);
					return prev; // 發生錯誤時保持原狀態
				}
			});
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 計算購物車總金額
	 */
	const total = useMemo(
		() => {
			return cartItems.reduce((sum, item) => {
				let price = 0;
				let qty = 1;

				if (isProductItem(item)) {
					// 商品有 quantity 欄位
					const product = item;
					if (product.quantity !== undefined) {
						qty = Number(product.quantity);
					}
					// 優先使用 assignPrice，如果沒有則使用 price
					if (product.assignPrice !== undefined) {
						price = Number(product.assignPrice);
					} else if (product.price !== undefined) {
						price = Number(product.price);
					}
				} else {
					// 費用只有 assignPrice，沒有 quantity
					const fee = item;
					if (fee.assignPrice !== undefined) {
						price = Number(fee.assignPrice);
					}
				}

				return sum + price * qty;
			}, 0);
		},
		// eslint-disable-next-line
		[cartItems],
	);

	/**
	 * 購物車項目數量
	 */
	const itemCount = useMemo(
		() => {
			return cartItems.length;
		},
		// eslint-disable-next-line
		[cartItems],
	);

	/**
	 * 購物車是否為空
	 */
	const isEmpty = useMemo(
		() => {
			return cartItems.length === 0;
		},
		// eslint-disable-next-line
		[cartItems],
	);

	return {
		// 狀態
		cartItems,
		total,
		itemCount,
		isEmpty,

		// 操作方法
		addItem,
		removeItem,
		clearCart,
		setCart,
		updatePrice, // 更新價格
		updateQuantity, // 更新數量（僅商品）
		updateNotes, // 更新備註
		deleteItem, // 刪除單個項目
		deleteItemsWhere, // 根據條件刪除項目

		// 檢查方法
		isProductInCart,
		isFeeInCart,
	};
};
